import { Plus, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import {
  useChargeFee,
  useCreateFeeSchedule,
  useFeeCharges,
  useFeeSchedules,
  useFeeSummary,
  useGenerateRecurringFees,
  usePayFeeCharge,
  useWaiveFee,
  useWaiveFeeCharge,
} from '@/hooks/use-fee-engine';
import { fmtCurrencyExact, fmtCurrencyTrimmed, fmtDate } from '@/lib/format';
import type { FeeChargeStatus, FeeFrequency, FeeType } from '@/types';
import { FEE_TYPE_LABELS } from '@/types';

const FEE_FREQUENCY_LABELS: Record<FeeFrequency, string> = {
  ONE_OFF: 'One-off',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

const FEE_CHARGE_STATUS_CONFIG: Record<
  FeeChargeStatus,
  { label: string; color: string; bgColor: string }
> = {
  PENDING: { label: 'Pending', color: '#6b7280', bgColor: '#f9fafb' },
  CHARGED: { label: 'Charged', color: '#f59e0b', bgColor: '#fffbeb' },
  PAID: { label: 'Paid', color: '#10b981', bgColor: '#ecfdf5' },
  WAIVED: { label: 'Waived', color: '#8b5cf6', bgColor: '#f5f3ff' },
  DEFERRED: { label: 'Deferred', color: '#3b82f6', bgColor: '#eff6ff' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bgColor: '#f9fafb' },
};

const FEE_TYPES: FeeType[] = [
  'ESTABLISHMENT',
  'LINE_FEE',
  'MONTHLY_SERVICE',
  'LATE_PAYMENT',
  'DISCHARGE',
  'VARIATION',
  'EXTENSION',
  'VALUATION',
  'LEGAL',
  'INSURANCE',
  'DEFAULT_INTEREST',
  'CUSTOM',
];

interface AddFeeModalProps {
  open: boolean;
  onClose: () => void;
  loanId: string;
  onSuccess: () => void;
}

function AddFeeModal({ open, onClose, loanId, onSuccess }: AddFeeModalProps) {
  const [feeType, setFeeType] = useState<FeeType>('ESTABLISHMENT');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<FeeFrequency>('ONE_OFF');
  const [nextDueDate, setNextDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [amountError, setAmountError] = useState('');
  const createFee = useCreateFeeSchedule(loanId);
  const amountNum = parseFloat(amount);
  const hasInvalidAmount = amount !== '' && (!Number.isFinite(amountNum) || amountNum <= 0);

  const resetForm = () => {
    setFeeType('ESTABLISHMENT');
    setName('');
    setAmount('');
    setFrequency('ONE_OFF');
    setNextDueDate('');
    setNotes('');
    setAmountError('');
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setAmountError('Enter an amount greater than 0.');
      return;
    }
    setAmountError('');

    await createFee.mutateAsync({
      feeType,
      name: name || FEE_TYPE_LABELS[feeType],
      amount: amountNum,
      frequency,
      autoCharge: frequency !== 'ONE_OFF',
      nextDueDate: nextDueDate || undefined,
      notes: notes || undefined,
    });
    resetForm();
    onSuccess();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalContentWrapper
      open
      onClose={onClose}
      title="Add Fee Schedule"
      closeOnBackdropClick={false}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type</label>
            <select
              value={feeType}
              onChange={e => setFeeType(e.target.value as FeeType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FEE_TYPES.map(t => (
                <option key={t} value={t}>
                  {FEE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fee Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={FEE_TYPE_LABELS[feeType]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (AUD)</label>
            <input
              type="number"
              value={amount}
              onChange={e => {
                setAmount(e.target.value);
                if (amountError) setAmountError('');
              }}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            {amountError && <div className="mt-2 text-sm text-red-600">{amountError}</div>}
            {amount && (
              <div className="mt-2 text-sm text-gray-600">
                <div>GST (10%): {fmtCurrencyExact((parseFloat(amount) * 0.1).toString())}</div>
                <div className="font-medium">
                  Total: {fmtCurrencyExact((parseFloat(amount) * 1.1).toString())}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={frequency}
              onChange={e => setFrequency(e.target.value as FeeFrequency)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(FEE_FREQUENCY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {frequency === 'ONE_OFF' ? 'Due Date' : 'First Due Date'}
            </label>
            <input
              type="date"
              value={nextDueDate}
              onChange={e => setNextDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="sm:pt-6">
            <p className="text-sm text-gray-500">
              {frequency === 'ONE_OFF'
                ? 'One-off fees create a single charge.'
                : 'Recurring fees generate charges when the due date is reached and recurring generation runs.'}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add any notes..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={createFee.isPending || hasInvalidAmount || amount.trim() === ''}
            className="w-fit flex-1 px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
          >
            {createFee.isPending ? (
              <>
                <Spinner size="lg" className="w-4 h-4 inline mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Add Fee'
            )}
          </Button>
        </div>
      </form>
    </ModalContentWrapper>
  );
}

interface WaiveFeeModalProps {
  open: boolean;
  onClose: () => void;
  loanId: string;
  targetId: string;
  mode: 'schedule' | 'charge';
  onSuccess: () => void;
}

function WaiveFeeModal({ open, onClose, loanId, targetId, mode, onSuccess }: WaiveFeeModalProps) {
  const [reason, setReason] = useState('');
  const waiveFeeSchedule = useWaiveFee(loanId);
  const waiveFeeCharge = useWaiveFeeCharge(loanId);

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'schedule') {
      await waiveFeeSchedule.mutateAsync({ feeId: targetId, reason });
    } else {
      await waiveFeeCharge.mutateAsync({ chargeId: targetId, reason });
    }
    setReason('');
    onSuccess();
    onClose();
  };

  const isPending = waiveFeeSchedule.isPending || waiveFeeCharge.isPending;

  if (!open) return null;

  return (
    <ModalContentWrapper open onClose={onClose} title="Waive Fee" closeOnBackdropClick={false}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Waiver Reason</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain why this fee is being waived..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <button
            type="submit"
            disabled={isPending}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isPending ? (
              <>
                <Spinner size="lg" className="w-4 h-4 inline mr-2 animate-spin" />
                Waiving...
              </>
            ) : mode === 'schedule' ? (
              'Waive Schedule'
            ) : (
              'Waive Charge'
            )}
          </button>
        </div>
      </form>
    </ModalContentWrapper>
  );
}

export function FeeScheduleTab({ loanId }: { loanId: string }) {
  const [addFeeOpen, setAddFeeOpen] = useState(false);
  const [waiveFeeOpen, setWaiveFeeOpen] = useState(false);
  const [waiveTargetId, setWaiveTargetId] = useState('');
  const [waiveMode, setWaiveMode] = useState<'schedule' | 'charge'>('schedule');
  const { data: schedulesRaw, isLoading: schedulesLoading } = useFeeSchedules(loanId);
  const { data: chargesRaw, isLoading: chargesLoading } = useFeeCharges(loanId);
  const { data: summaryRaw, isLoading: summaryLoading } = useFeeSummary(loanId);
  const generateRecurringFees = useGenerateRecurringFees(loanId);
  const chargeFee = useChargeFee(loanId);
  const payFeeCharge = usePayFeeCharge(loanId);

  const schedules = Array.isArray(schedulesRaw) ? schedulesRaw : [];
  const charges = Array.isArray(chargesRaw) ? chargesRaw : [];
  const summary = summaryRaw ?? {
    totalFees: 0,
    activeFees: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    totalWaived: 0,
    outstandingCharges: 0,
    feeCount: 0,
    chargeCount: 0,
  };

  const isLoading = schedulesLoading || chargesLoading || summaryLoading;

  const openWaiveModal = (targetId: string, mode: 'schedule' | 'charge') => {
    setWaiveTargetId(targetId);
    setWaiveMode(mode);
    setWaiveFeeOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-400">Loading fee data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fee Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Fees</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {fmtCurrencyTrimmed(summary.totalFees ?? 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Active Fees
            </p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {fmtCurrencyTrimmed(summary.activeFees ?? summary.totalOutstanding ?? 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {fmtCurrencyTrimmed(summary.totalPaid ?? 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Waived</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">
              {fmtCurrencyTrimmed(summary.totalWaived ?? 0)}
            </p>
          </div>
        </div>
      )}

      {/* Fee Schedules Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Fee Schedules</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => generateRecurringFees.mutate()}
              disabled={generateRecurringFees.isPending}
              className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${generateRecurringFees.isPending ? 'animate-spin' : ''}`}
              />
              {generateRecurringFees.isPending ? 'Generating...' : 'Generate Recurring'}
            </Button>
            <Button
              variant="primary"
              onClick={() => setAddFeeOpen(true)}
              className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Fee
            </Button>
          </div>
        </div>

        {schedules && schedules.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Name</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Type</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">Amount</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">GST</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">Total</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Status</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Frequency</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Next Due</th>
                  <th className="px-6 py-3 text-center font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map(fee => (
                  <tr key={fee.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{fee.name}</div>
                      {fee.waivedReason && (
                        <div className="mt-1 text-xs text-gray-500">Waived: {fee.waivedReason}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        {FEE_TYPE_LABELS[fee.feeType]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      {fmtCurrencyExact(fee.amount)}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600">
                      {fmtCurrencyExact(fee.gstAmount)}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      {fmtCurrencyExact(fee.totalAmount)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block rounded px-2 py-1 text-xs font-medium ${
                          fee.isActive
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {fee.isActive ? 'Active' : 'Waived'}
                      </span>
                      {!fee.isActive && fee.waivedAt && (
                        <div className="mt-1 text-xs text-gray-500">{fmtDate(fee.waivedAt)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {fee.frequency === 'ONE_OFF' ? (
                        <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                          One-off
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          {FEE_FREQUENCY_LABELS[fee.frequency]}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {!fee.isActive ? (
                        <span className="text-gray-400">
                          {fee.nextDueDate ? `${fmtDate(fee.nextDueDate)} (waived)` : '—'}
                        </span>
                      ) : fee.frequency === 'ONE_OFF' ? (
                        '—'
                      ) : fee.nextDueDate ? (
                        fmtDate(fee.nextDueDate)
                      ) : (
                        'Not scheduled'
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {fee.isActive ? (
                        <button
                          onClick={() => openWaiveModal(fee.id, 'schedule')}
                          className="text-red-600 hover:text-red-900 text-xs font-medium"
                        >
                          Waive
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 text-sm">No fee schedules created yet</p>
          </div>
        )}
      </div>

      {/* Fee Charges Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Fee Charges</h3>
        </div>

        {charges && charges.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Date</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Fee</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">Amount</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">GST</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-700">Total</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-700">Status</th>
                  <th className="px-6 py-3 text-center font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {charges.map(charge => {
                  const cfg = FEE_CHARGE_STATUS_CONFIG[charge.status];
                  return (
                    <tr key={charge.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(charge.dueDate).toLocaleDateString('en-AU')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {charge.feeSchedule?.name || 'Fee'}
                        </div>
                        {charge.feeSchedule?.feeType && (
                          <div className="mt-1 text-xs text-gray-500">
                            {FEE_TYPE_LABELS[charge.feeSchedule.feeType]}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        {fmtCurrencyExact(charge.amount)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-600">
                        {fmtCurrencyExact(charge.gstAmount)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        {fmtCurrencyExact(charge.totalAmount)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-block px-2.5 py-1 text-xs font-medium rounded"
                          style={{ color: cfg.color, backgroundColor: cfg.bgColor }}
                        >
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {charge.status === 'PENDING' ? (
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => chargeFee.mutate(charge.id)}
                              disabled={chargeFee.isPending && chargeFee.variables === charge.id}
                              className="text-indigo-600 hover:text-indigo-900 text-xs font-medium disabled:opacity-50"
                            >
                              {chargeFee.isPending && chargeFee.variables === charge.id
                                ? 'Charging...'
                                : 'Charge'}
                            </button>
                            <button
                              onClick={() => openWaiveModal(charge.id, 'charge')}
                              className="text-red-600 hover:text-red-900 text-xs font-medium"
                            >
                              Waive
                            </button>
                          </div>
                        ) : charge.status === 'CHARGED' ? (
                          <button
                            onClick={() => payFeeCharge.mutate(charge.id)}
                            disabled={
                              payFeeCharge.isPending && payFeeCharge.variables === charge.id
                            }
                            className="text-green-600 hover:text-green-900 text-xs font-medium disabled:opacity-50"
                          >
                            {payFeeCharge.isPending && payFeeCharge.variables === charge.id
                              ? 'Paying...'
                              : 'Pay'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 text-sm">No fee charges recorded yet</p>
          </div>
        )}
      </div>

      <AddFeeModal
        open={addFeeOpen}
        onClose={() => setAddFeeOpen(false)}
        loanId={loanId}
        onSuccess={() => {}}
      />

      <WaiveFeeModal
        open={waiveFeeOpen}
        onClose={() => setWaiveFeeOpen(false)}
        loanId={loanId}
        targetId={waiveTargetId}
        mode={waiveMode}
        onSuccess={() => {}}
      />
    </div>
  );
}
